import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { TooltipProvider } from "@/components/ui/tooltip";
import SidebarCollapseTab from "@/components/shared/SidebarCollapseTab";
import SidebarNavTooltip from "@/components/shared/SidebarNavTooltip";
import { usePersistedSidebarCollapsed } from "@/hooks/usePersistedSidebarCollapsed";
import { cn } from "@/lib/utils";
import BrandLogo from "@/components/BrandLogo";
import {
  AlignJustify,
  LayoutDashboard,
  LayoutGrid,
  RefreshCw,
} from "lucide-react";

const SIDEBAR_EXPANDED = 260;
const SIDEBAR_COLLAPSED = 72;

function AdminSidebar({ navItems, activeTab, onNav, navScrollable = false, collapsed }) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-[#0f172a]">
      <div
        className={cn(
          "shrink-0 border-b border-white/10 py-4",
          collapsed ? "px-2" : "px-4",
        )}
      >
        <div className={cn("flex items-center", collapsed ? "justify-center" : "gap-2.5")}>
          <BrandLogo size="sm" data-testid="admin-sidebar-logo" />
          {!collapsed ? (
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm font-bold tracking-tight text-white">RdvMarket</div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Console admin
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <nav
        className={cn(
          "flex-1 min-h-0 space-y-0.5 py-3",
          collapsed ? "px-1.5" : "px-2",
          navScrollable ? "overflow-y-auto" : "overflow-hidden",
        )}
        aria-label="Navigation admin"
        data-testid="admin-tabs"
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = activeTab === item.value;
          const count = typeof item.count === "number" ? item.count : 0;
          const showBadge = item.showBadge !== false && count > 0;
          return (
            <SidebarNavTooltip key={item.value} collapsed={collapsed} label={item.label}>
              <button
                type="button"
                onClick={() => onNav(item.value)}
                data-testid={item.testId}
                className={cn(
                  "group relative flex w-full items-center rounded-lg text-left text-sm font-medium transition-colors",
                  collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5",
                  active
                    ? "bg-white/10 text-white shadow-sm ring-1 ring-white/10"
                    : "text-slate-400 hover:bg-white/5 hover:text-slate-100",
                )}
              >
                <Icon
                  className={cn(
                    "h-[18px] w-[18px] shrink-0",
                    active ? "text-white" : "text-slate-500 group-hover:text-slate-300",
                  )}
                  aria-hidden
                />
                {!collapsed ? <span className="min-w-0 flex-1 truncate">{item.label}</span> : null}
                {showBadge ? (
                  <span
                    className={cn(
                      "shrink-0 rounded-full font-semibold tabular-nums",
                      collapsed
                        ? "absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center px-1 text-[9px]"
                        : "px-2 py-0.5 text-[11px]",
                      item.badgeVariant === "alert"
                        ? "bg-amber-500 text-amber-950"
                        : active
                          ? "bg-[#2D7FF9] text-white"
                          : "bg-white/15 text-slate-200",
                    )}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            </SidebarNavTooltip>
          );
        })}
      </nav>

      <div className={cn("shrink-0 space-y-0.5 border-t border-white/10", collapsed ? "p-2" : "p-3")}>
        <SidebarNavTooltip collapsed={collapsed} label="Espace membre">
          <Link
            to="/dashboard"
            className={cn(
              "flex items-center rounded-lg text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white",
              collapsed ? "justify-center px-2 py-2.5" : "gap-2 px-3 py-2",
            )}
            data-testid="admin-sidebar-member"
          >
            <LayoutDashboard className="h-4 w-4 shrink-0" aria-hidden />
            {!collapsed ? <span>Espace membre</span> : null}
          </Link>
        </SidebarNavTooltip>
      </div>
    </div>
  );
}

export default function AdminShell({
  activeTab,
  onNavigate,
  navItems = [],
  title,
  subtitle,
  kpis = null,
  onRefresh,
  refreshing = false,
  /** Bloque le scroll de la page : le contenu principal doit gérer overflow en interne (ex. split liste + fiche). */
  lockMainScroll = false,
  children,
}) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { collapsed, toggle: toggleCollapsed } = usePersistedSidebarCollapsed();

  const handleNav = (value) => {
    onNavigate(value);
    setMobileNavOpen(false);
  };

  const desktopSidebar = (
    <AdminSidebar
      navItems={navItems}
      activeTab={activeTab}
      onNav={handleNav}
      navScrollable={false}
      collapsed={collapsed}
    />
  );
  const mobileSidebar = (
    <AdminSidebar navItems={navItems} activeTab={activeTab} onNav={handleNav} navScrollable collapsed={false} />
  );

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  /** Modales (portal body) : centrage dans la zone à droite de la sidebar fixe. */
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--sidebar-w", `${sidebarWidth}px`);
    return () => {
      root.style.removeProperty("--sidebar-w");
    };
  }, [sidebarWidth]);

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          "flex bg-[#f1f5f9]",
          lockMainScroll ? "h-[100dvh] max-h-[100dvh] overflow-hidden" : "min-h-screen",
        )}
      >
        <div
          className="fixed left-0 top-0 z-30 hidden h-[100dvh] min-h-[100dvh] lg:block"
          style={{ width: sidebarWidth }}
        >
          <aside className="h-full overflow-hidden border-r border-slate-800/80 bg-[#0f172a] transition-[width] duration-200 ease-out">
            {desktopSidebar}
          </aside>
          <SidebarCollapseTab
            collapsed={collapsed}
            onToggle={toggleCollapsed}
            testId="admin-sidebar-toggle"
          />
        </div>

        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className="w-[min(100vw-2rem,280px)] border-slate-800 bg-[#0f172a] p-0 text-white">
            <SheetHeader className="sr-only">
              <SheetTitle>Menu administration</SheetTitle>
            </SheetHeader>
            {mobileSidebar}
          </SheetContent>
        </Sheet>

        <div
          className="flex min-h-0 min-w-0 flex-1 flex-col transition-[padding] duration-200 ease-out lg:pl-[var(--sidebar-w)]"
          style={{ "--sidebar-w": `${sidebarWidth}px` }}
        >
          <header className="sticky top-0 z-20 border-b border-slate-200/90 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
            <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0 border-slate-200 lg:hidden"
                  onClick={() => setMobileNavOpen(true)}
                  aria-label="Ouvrir le menu"
                >
                  <AlignJustify className="h-5 w-5" />
                </Button>
                <div className="min-w-0">
                  <h1 className="font-display text-xl font-bold tracking-tight text-[#0B0D12] sm:text-2xl">{title}</h1>
                  {subtitle ? <p className="mt-0.5 text-sm text-slate-600">{subtitle}</p> : null}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                {onRefresh ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 border-slate-200 text-slate-700"
                    onClick={onRefresh}
                    disabled={refreshing}
                    data-testid="admin-refresh"
                  >
                    <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} aria-hidden />
                    Actualiser
                  </Button>
                ) : null}
              </div>
            </div>
            {Array.isArray(kpis) && kpis.length > 0 ? (
              <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-3 sm:px-6">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
                  {kpis.map(({ key, label, valueLabel, Icon, hint }) => (
                    <div
                      key={key}
                      className="flex min-w-0 gap-2.5 rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 shadow-sm"
                      title={hint}
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#2D7FF9]/10 text-[#2D7FF9]">
                        <Icon className="h-4 w-4" aria-hidden />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-bold tabular-nums text-[#0B0D12]">{valueLabel}</div>
                        <div className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          {label}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </header>

          <main
            className={cn(
              "min-h-0 min-w-0 flex-1 px-4 py-5 sm:px-6 sm:py-6",
              lockMainScroll
                ? "flex flex-col overflow-hidden"
                : "overflow-x-hidden overflow-y-auto",
            )}
          >
            {children}
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
